/**
 * Campaigns tab.
 *
 * Lists existing campaigns + lets admin create a new one. Each campaign card
 * shows current status + headline stats, with quick actions to activate/pause.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Plus, RefreshCw, Play, Pause, CheckCircle2, Trash2, X,
  ChevronDown, ChevronUp, Mail, FileText,
} from 'lucide-react';

import type { ApiCall } from '../../api/interview';
import {
  type Campaign,
  listCampaigns, createCampaign, updateCampaign,
  activateCampaign, pauseCampaign, completeCampaign, deleteCampaign,
  triggerGenerateDrafts, triggerSendBatch,
} from '../../api/b2b';
import { Centered, ErrorBox, HeadingWithInfo, ActionWithInfo } from './_ui';


interface Props { apiCall: ApiCall; }


const B2BTabCampaigns: React.FC<Props> = ({ apiCall }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCampaigns(await listCampaigns(apiCall, { include_stats: true }));
    } catch (e: any) {
      setError(e?.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <HeadingWithInfo
          className="text-sm text-slate-500"
          info="Outreach-Kampagnen: Drafts generieren, freigeben und versenden. Status draft → active → completed."
        >
          {campaigns.length} campaigns
        </HeadingWithInfo>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50">
            <RefreshCw className="w-4 h-4 text-slate-600" />
          </button>
          <ActionWithInfo info="Neue Kampagne anlegen - Absender-Mailbox, Modus (manuell/auto) und Rate-Limit.">
            <button
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-1 px-3 py-1.5 bg-pink-600 text-white text-sm rounded-md hover:bg-pink-700"
            >
              <Plus className="w-4 h-4" /> New
            </button>
          </ActionWithInfo>
        </div>
      </div>

      {loading ? (
        <Centered><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></Centered>
      ) : error ? (
        <ErrorBox message={error} onRetry={load} />
      ) : campaigns.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-500 text-sm">
          No campaigns yet. Create one to start generating drafts.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {campaigns.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              apiCall={apiCall}
              onChanged={load}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <CreateCampaignModal
          onClose={() => setCreateOpen(false)}
          onCreate={async (payload) => {
            await createCampaign(apiCall, payload);
            await load();
            setCreateOpen(false);
          }}
        />
      )}
    </>
  );
};

export default B2BTabCampaigns;


const STATUS_COLORS: Record<string, string> = {
  draft:     'bg-slate-100 text-slate-600',
  active:    'bg-emerald-100 text-emerald-700',
  paused:    'bg-amber-100 text-amber-700',
  completed: 'bg-blue-100 text-blue-700',
  archived:  'bg-slate-200 text-slate-500',
};

const MODE_LABELS: Record<string, string> = {
  manual_review:        'Manual review',
  auto_above_threshold: 'Auto > threshold',
  full_auto:            'Full auto',
};


const CampaignCard: React.FC<{
  campaign: Campaign;
  apiCall: ApiCall;
  onChanged: () => void;
}> = ({ campaign, apiCall, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateSubject, setTemplateSubject] = useState(campaign.template_subject || '');
  const [templateBody, setTemplateBody] = useState(campaign.template_body || '');

  const doAction = async (fn: () => Promise<any>) => {
    setBusy(true);
    try { await fn(); await onChanged(); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-slate-900 truncate">{campaign.name}</h3>
            <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${STATUS_COLORS[campaign.status] || ''}`}>
              {campaign.status}
            </span>
          </div>
          <div className="text-xs text-slate-500 truncate">
            {campaign.from_mailbox} · {MODE_LABELS[campaign.mode] || campaign.mode}
            {campaign.mode === 'auto_above_threshold' && ` · conf ≥ ${(Number(campaign.auto_send_threshold || 0)).toFixed(2)}`}
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1 text-slate-400 hover:text-slate-600"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {campaign.stats && (
        <div className="grid grid-cols-4 gap-2 text-xs">
          <Mini label="Drafts" value={campaign.stats.drafts_total} />
          <Mini label="Pending" value={campaign.stats.drafts_pending_review} accent="amber" />
          <Mini label="Sent" value={campaign.stats.drafts_sent} accent="blue" />
          <Mini label="Replies" value={campaign.stats.replies_total} accent="emerald" sub={`${campaign.stats.replies_positive} +`} />
        </div>
      )}

      <div className="flex items-center gap-1 pt-1 flex-wrap">
        {campaign.status === 'draft' && (
          <ActionWithInfo info="Kampagne starten - danach Generate drafts / Send batch möglich.">
            <button onClick={() => doAction(() => activateCampaign(apiCall, campaign.id))} disabled={busy}
                    className="text-xs px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
              <Play className="w-3 h-3" /> Activate
            </button>
          </ActionWithInfo>
        )}
        {campaign.status === 'active' && (
          <ActionWithInfo info="Versand pausieren - Drafts bleiben, Send batch stoppt.">
            <button onClick={() => doAction(() => pauseCampaign(apiCall, campaign.id))} disabled={busy}
                    className="text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1">
              <Pause className="w-3 h-3" /> Pause
            </button>
          </ActionWithInfo>
        )}
        {campaign.status === 'paused' && (
          <ActionWithInfo info="Pausierte Kampagne wieder aktivieren.">
            <button onClick={() => doAction(() => activateCampaign(apiCall, campaign.id))} disabled={busy}
                    className="text-xs px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
              <Play className="w-3 h-3" /> Resume
            </button>
          </ActionWithInfo>
        )}
        {(campaign.status === 'active' || campaign.status === 'paused') && (
          <ActionWithInfo info="Kampagne abschließen - kein weiterer Versand.">
            <button onClick={() => doAction(() => completeCampaign(apiCall, campaign.id))} disabled={busy}
                    className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Complete
            </button>
          </ActionWithInfo>
        )}
        {(campaign.status === 'active' || campaign.status === 'paused' || campaign.status === 'draft') && (
          <>
            <ActionWithInfo info="LLM schreibt personalisierte Emails für passende Contacts (max 50 pro Klick).">
              <button
                onClick={async () => {
                  if (!window.confirm(`Generate drafts for campaign "${campaign.name}"?`)) return;
                  setBusy(true);
                  try {
                    await triggerGenerateDrafts(apiCall, { campaign_id: campaign.id, limit: 50 });
                    onChanged();
                  } catch (e: any) {
                    alert(e?.message || 'Generate drafts failed');
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy}
                className="text-xs px-2 py-1 bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-1"
              >
                <FileText className="w-3 h-3" /> Generate drafts
              </button>
            </ActionWithInfo>
            <ActionWithInfo info="Versendet human/auto-approved Drafts - respektiert Rate-Limit und Blocklist.">
              <button
                onClick={async () => {
                  if (!window.confirm(`Send approved drafts for "${campaign.name}"?`)) return;
                  setBusy(true);
                  try {
                    await triggerSendBatch(apiCall, { campaign_id: campaign.id });
                    onChanged();
                  } catch (e: any) {
                    alert(e?.message || 'Send batch failed');
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy}
                className="text-xs px-2 py-1 bg-pink-600 text-white rounded hover:bg-pink-700 disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Mail className="w-3 h-3" /> Send batch
              </button>
            </ActionWithInfo>
          </>
        )}
        <div className="flex-1" />
        <button
          onClick={async () => {
            if (!window.confirm(`Delete campaign "${campaign.name}"? This removes drafts and sends linked to it.`)) return;
            await doAction(() => deleteCampaign(apiCall, campaign.id));
          }}
          disabled={busy}
          className="text-xs p-1.5 text-red-500 hover:text-red-700 disabled:opacity-50"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="pt-2 mt-2 border-t border-slate-100 space-y-2 text-xs">
          <div>
            <span className="text-slate-500">Rate limit:</span> {campaign.rate_limit_per_day} /day
          </div>
          <div>
            <span className="text-slate-500">Follow-ups:</span>{' '}
            {campaign.followup_enabled
              ? `enabled, delays ${campaign.followup_delays_days.join(', ')} days`
              : 'disabled'}
          </div>
          {campaign.notes && (
            <div className="text-slate-500 whitespace-pre-wrap">{campaign.notes}</div>
          )}

          <div className="pt-2 border-t border-slate-100 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Template (currently advisory - drafts are LLM-generated):</span>
              <button onClick={() => setEditingTemplate(!editingTemplate)} className="text-pink-600 hover:underline">
                {editingTemplate ? 'Cancel' : 'Edit'}
              </button>
            </div>
            {editingTemplate ? (
              <div className="space-y-1.5">
                <input
                  value={templateSubject}
                  onChange={(e) => setTemplateSubject(e.target.value)}
                  placeholder="Template subject"
                  className="w-full text-xs border border-slate-200 rounded px-2 py-1.5"
                />
                <textarea
                  value={templateBody}
                  onChange={(e) => setTemplateBody(e.target.value)}
                  placeholder="Template body (used as style hint)"
                  rows={5}
                  className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 font-mono"
                />
                <button
                  onClick={async () => {
                    await doAction(() => updateCampaign(apiCall, campaign.id, {
                      template_subject: templateSubject,
                      template_body: templateBody,
                    }));
                    setEditingTemplate(false);
                  }}
                  className="text-xs px-2 py-1 bg-pink-600 text-white rounded hover:bg-pink-700"
                >
                  Save template
                </button>
              </div>
            ) : (
              <div className="text-slate-600">
                <div className="font-medium truncate">{campaign.template_subject || '(no subject template)'}</div>
                <pre className="text-[11px] whitespace-pre-wrap text-slate-500 max-h-32 overflow-y-auto">
                  {campaign.template_body || '(no body template)'}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const Mini: React.FC<{ label: string; value: number; accent?: 'amber'|'blue'|'emerald'; sub?: string }> = ({ label, value, accent, sub }) => {
  const color =
    accent === 'amber'   ? 'text-amber-700' :
    accent === 'blue'    ? 'text-blue-700' :
    accent === 'emerald' ? 'text-emerald-700' : 'text-slate-700';
  return (
    <div className="bg-slate-50 rounded px-2 py-1.5">
      <div className="text-[10px] uppercase text-slate-400 tracking-wider">{label}</div>
      <div className={`text-sm font-semibold ${color}`}>{value}{sub && <span className="ml-1 text-[10px] text-slate-500">{sub}</span>}</div>
    </div>
  );
};


const CreateCampaignModal: React.FC<{
  onClose: () => void;
  onCreate: (payload: Partial<Campaign>) => Promise<void>;
}> = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [mode, setMode] = useState('manual_review');
  const [autoSendThreshold, setAutoSendThreshold] = useState(0.85);
  const [fromMailbox, setFromMailbox] = useState('vu.ngo@cevee.eu');
  const [rateLimit, setRateLimit] = useState(30);
  const [followupEnabled, setFollowupEnabled] = useState(false);
  const [delays, setDelays] = useState('4,10');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const delaysParsed = delays.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);
      await onCreate({
        name,
        mode,
        auto_send_threshold: autoSendThreshold,
        from_mailbox: fromMailbox,
        rate_limit_per_day: rateLimit,
        followup_enabled: followupEnabled,
        followup_delays_days: delaysParsed,
        notes,
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to create campaign');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">New campaign</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. DACH Tech Hiring · Q3" className="input-b2b" />
          </Field>
          <Field label="Mode">
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="input-b2b">
              <option value="manual_review">Manual review (all drafts go to review queue)</option>
              <option value="auto_above_threshold">Auto-approve above threshold</option>
              <option value="full_auto">Full auto (every draft auto-approved)</option>
            </select>
          </Field>
          {mode === 'auto_above_threshold' && (
            <Field label="Auto-send threshold">
              <input type="number" min={0} max={1} step={0.05} value={autoSendThreshold}
                     onChange={(e) => setAutoSendThreshold(Number(e.target.value))} className="input-b2b" />
            </Field>
          )}
          <Field label="From mailbox">
            <select value={fromMailbox} onChange={(e) => setFromMailbox(e.target.value)} className="input-b2b">
              <option value="vu.ngo@cevee.eu">Vu Ngo &lt;vu.ngo@cevee.eu&gt;</option>
              <option value="lea.anders@cevee.eu">Lea Anders &lt;lea.anders@cevee.eu&gt;</option>
            </select>
          </Field>
          <Field label="Rate limit per day">
            <input type="number" min={1} max={200} value={rateLimit}
                   onChange={(e) => setRateLimit(Number(e.target.value))} className="input-b2b" />
          </Field>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="fu" checked={followupEnabled} onChange={(e) => setFollowupEnabled(e.target.checked)} />
            <label htmlFor="fu" className="text-sm">Enable follow-ups</label>
          </div>
          {followupEnabled && (
            <Field label="Follow-up delays (days, comma-separated)">
              <input value={delays} onChange={(e) => setDelays(e.target.value)} className="input-b2b" />
            </Field>
          )}
          <Field label="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                      placeholder="Internal notes" className="input-b2b" />
          </Field>
          {error && <div className="text-xs text-red-600">{error}</div>}
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={submit} disabled={busy || !name.trim()}
                    className="flex-1 px-3 py-2 text-sm bg-pink-600 text-white rounded hover:bg-pink-700 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Create'}
            </button>
          </div>
        </div>
      </div>
      <style>{`
        .input-b2b {
          width: 100%; font-size: 13px;
          border: 1px solid #e2e8f0; border-radius: 6px;
          padding: 6px 8px;
        }
      `}</style>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="text-xs text-slate-500 mb-1 block">{label}</span>
    {children}
  </label>
);
